import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'wasp/client/operations';
import { getServiceTypes, createServiceDefinition } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { Layers, Save, AlertCircle, HelpCircle } from 'lucide-react';

// Tooltip component
const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

interface FormErrors {
  name?: string;
  slug?: string;
  serviceTypeId?: string;
  submit?: string;
}

export default function ServiceDefinitionsNewPage() {
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Auth check
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">You must be logged in to add service definitions.</p>
        </div>
      </div>
    );
  }

  // Load service types for dropdown
  const { data: serviceTypes = [] } = useQuery(getServiceTypes);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    serviceTypeId: '',
    description: '',
    detailedDescription: '',
    typicalDuration: '',
    isRecurring: false,
    requiresBooking: false
  });

  const handleChange = (field: string, value: string | boolean) => {
    setFormData(f => ({ ...f, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(e => ({ ...e, [field]: undefined }));
    }
  };

  const handleNameChange = (name: string) => {
    setFormData(f => ({
      ...f,
      name,
      slug: f.slug || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    }));
    if (errors.name) {
      setErrors(e => ({ ...e, name: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug must be lowercase letters, numbers, and dashes only';
    }

    if (!formData.serviceTypeId) {
      newErrors.serviceTypeId = 'Service Type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await createServiceDefinition({
        ...formData,
        typicalDuration: formData.typicalDuration ? parseInt(formData.typicalDuration) : undefined
      });
      navigate('/admin/service-definitions');
    } catch (error: any) {
      console.error('Error creating service definition:', error);
      setErrors({ submit: error.message || 'Failed to create service definition. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/admin/service-definitions');
  };

  return (
    <div className="flex h-full">
        {/* Main Content Area */}
        <div className="flex-1 bg-card">
          <div className="flex h-full">
            {/* Form Content - Left side */}
            <div className="flex-1 pl-6 pt-4">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Layers className="w-8 h-8 text-blue-600" />
                  <div>
                    <h1 className="text-2xl font-bold text-content-text">Add Service Definition</h1>
                    <p className="text-content-text-muted">Create a new service template</p>
                  </div>
                </div>
              </div>

              {errors.submit && (
                <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg">
                  <p className="text-destructive flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {errors.submit}
                  </p>
                </div>
              )}

              <form id="service-definition-form" onSubmit={handleSubmit}>
                {/* Primary Input Fields - 2 column grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="text-lg font-medium text-content-text mb-4">Name *</h3>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder ${
                        errors.name ? 'border-destructive bg-destructive/10' : ''
                      }`}
                      placeholder="e.g., Jummah Prayer, Philosophy Study Circle"
                      required
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.name}
                      </p>
                    )}
                    <p className="text-sm text-content-text-muted mt-2">Service template name</p>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-content-text mb-4">Slug *</h3>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => handleChange('slug', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder ${
                        errors.slug ? 'border-destructive bg-destructive/10' : ''
                      }`}
                      placeholder="e.g., jummah-prayer"
                      required
                    />
                    {errors.slug && (
                      <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.slug}
                      </p>
                    )}
                    <p className="text-sm text-content-text-muted mt-2">URL-friendly identifier (auto-generated)</p>
                  </div>
                </div>

                {/* Description Fields - Full width */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-content-text mb-4">Description</h3>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder"
                    placeholder="Brief description..."
                  />
                  <p className="text-sm text-content-text-muted mt-2">{formData.description.length}/500 characters</p>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-medium text-content-text mb-4">Detailed Description</h3>
                  <textarea
                    value={formData.detailedDescription}
                    onChange={(e) => handleChange('detailedDescription', e.target.value)}
                    rows={5}
                    className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder"
                    placeholder="Full description with details..."
                  />
                  <p className="text-sm text-content-text-muted mt-2">{formData.detailedDescription.length}/2000 characters</p>
                </div>

                {/* Service Type and Duration - 2 column grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="text-lg font-medium text-content-text mb-4">Service Type *</h3>
                    <select
                      value={formData.serviceTypeId}
                      onChange={(e) => handleChange('serviceTypeId', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border ${
                        errors.serviceTypeId ? 'border-destructive bg-destructive/10' : ''
                      }`}
                      required
                    >
                      <option value="">Select a service type</option>
                      {(serviceTypes as any[]).map((type: any) => (
                        <option key={type.id} value={type.id}>{type.label}</option>
                      ))}
                    </select>
                    {errors.serviceTypeId && (
                      <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.serviceTypeId}
                      </p>
                    )}
                    <p className="text-sm text-content-text-muted mt-2">Category for this service</p>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-content-text mb-4">Typical Duration (minutes)</h3>
                    <input
                      type="number"
                      value={formData.typicalDuration}
                      onChange={(e) => handleChange('typicalDuration', e.target.value)}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder"
                      placeholder="60"
                      min="0"
                    />
                    <p className="text-sm text-content-text-muted mt-2">Expected service duration</p>
                  </div>
                </div>
              </form>
            </div>

            {/* Right Sidebar */}
            <div className="w-80 border-l border-border flex flex-col">
              {/* Action Buttons */}
              <div className="p-6 border-b border-border">
                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    form="service-definition-form"
                    disabled={isLoading || !formData.name.trim() || !formData.slug.trim() || !formData.serviceTypeId}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Submit
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleGoBack}
                    className="w-full px-4 py-3 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Settings Section */}
              <div className="p-6 border-b border-border">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Settings</h3>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isRecurring}
                      onChange={(e) => handleChange('isRecurring', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 rounded"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Is Recurring</div>
                      <div className="text-xs text-content-text-muted">Regular schedule</div>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.requiresBooking}
                      onChange={(e) => handleChange('requiresBooking', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 rounded"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Requires Booking</div>
                      <div className="text-xs text-content-text-muted">Advance registration</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Help Section */}
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5" />
                  Help
                </h3>

                <div className="space-y-3 text-sm text-content-text-muted">
                  <p>• Name, slug, and service type are required</p>
                  <p>• Slug is auto-generated from name</p>
                  <p>• Service definitions are templates for services</p>
                  <p>• Duration is in minutes</p>
                  <p>• Mark as recurring if it happens regularly</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}



